import {
  COST_BANDS,
  FOOD_CUISINES,
  FOOD_DIET_CLASSES,
  FOOD_KINDS,
  FOOD_MEAL_TIMES,
  MEAL_ROLES,
  PORTION_UNITS,
  PREP_TIME_BANDS,
  RECIPE_CONFIDENCES,
  type FoodCuisineName,
  type FoodMealTimeName,
  type RecipeLineMatchName,
} from '@steado/admin-contract/foods';
import { ALLERGENS, type AllergenName } from '@steado/admin-contract/user-detail';
import { useId } from 'react';

import {
  Labelled,
  MICRO_LABEL,
  NATIVE_SELECT,
  NullableSelect,
  SelectField,
  TokenPicker,
} from '@/components/form/fields';
import { toggleToken } from '@/lib/toggle-token';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { Panel } from '@/components/panel/panels';
import { IngredientPicker } from '@/screens/foods/IngredientPicker';
import { JAIN_AVOIDS_HINT } from '@/screens/foods/JainAnswer';

import {
  MONTHS,
  MONTH_LABELS,
  type FoodFormValues,
  type PortionValues,
  type ServingPolicyValues,
  type RecipeLineValues,
} from './food-form';

/** The two stated Jain answers; the select's empty option is "not stated". */
const JAIN_ANSWERS = ['avoids', 'allows'] as const;

interface FoodFieldsProps {
  readonly values: FoodFormValues;
  readonly onChange: (next: FoodFormValues) => void;
  /** False for `support`, which reads the catalog and changes nothing. */
  readonly disabled: boolean;
}

/**
 * Every field a new food carries, on one page.
 *
 * Grouped by what a curator is deciding rather than by which column it writes:
 * what the food is, what is in it, who may eat it, where plans should reach
 * for it, and how much of it counts as a serving.
 */
export function FoodFields({ values, onChange, disabled }: FoodFieldsProps) {
  const nameId = useId();
  const categoryId = useId();
  const onionId = useId();
  const beefId = useId();
  const servableId = useId();
  const commonId = useId();

  const set = <K extends keyof FoodFormValues>(key: K, value: FoodFormValues[K]) => {
    onChange({ ...values, [key]: value });
  };

  const assessed = values.allergenSource === 'source_stated';

  return (
    <div className="flex flex-col gap-4">
      <Panel title="Identity">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
          <Labelled id={nameId} label="Name" hint="The slug is derived from this and frozen after saving.">
            <Input
              id={nameId}
              value={values.name}
              disabled={disabled}
              onChange={(event) => {
                set('name', event.target.value);
              }}
            />
          </Labelled>

          <SelectField
            label="Kind"
            hint="A dish is cooked from ingredients; an ingredient is one of them."
            options={FOOD_KINDS}
            value={values.kind}
            disabled={disabled}
            onChange={(next) => {
              set('kind', next);
            }}
          />

          <Labelled id={categoryId} label="Category" hint="The source's own grouping. Optional.">
            <Input
              id={categoryId}
              value={values.category}
              disabled={disabled}
              onChange={(event) => {
                set('category', event.target.value);
              }}
            />
          </Labelled>
        </div>
      </Panel>

      <Panel
        title="Nutrition"
        description="Per 100 g of the edible food — never per serving, and never a whole recipe's yield. Every calculation in the product reads these."
      >
        <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-4">
          <NumberField
            label="Calories"
            hint="kcal"
            value={values.kcal}
            disabled={disabled}
            onChange={(next) => {
              set('kcal', next);
            }}
          />
          <NumberField
            label="Protein"
            hint="g"
            value={values.protein}
            disabled={disabled}
            onChange={(next) => {
              set('protein', next);
            }}
          />
          <NumberField
            label="Carbs"
            hint="g"
            value={values.carbs}
            disabled={disabled}
            onChange={(next) => {
              set('carbs', next);
            }}
          />
          <NumberField
            label="Fat"
            hint="g"
            value={values.fat}
            disabled={disabled}
            onChange={(next) => {
              set('fat', next);
            }}
          />
          <NumberField
            label="Fibre"
            hint="g — leave blank if unknown, rather than zero"
            value={values.fibre}
            disabled={disabled}
            onChange={(next) => {
              set('fibre', next);
            }}
          />
        </div>
      </Panel>

      <Panel title="Diet and allergens">
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
            <SelectField
              label="Diet class"
              hint="What the food is, not what it can be made without."
              options={FOOD_DIET_CLASSES}
              value={values.dietClass}
              disabled={disabled}
              onChange={(next) => {
                set('dietClass', next);
              }}
            />
            <NullableSelect
              label="Jain avoids"
              hint={`${JAIN_AVOIDS_HINT} Excluded from both Jain diets.`}
              options={JAIN_ANSWERS}
              value={values.jainExcluded === null ? null : values.jainExcluded ? 'avoids' : 'allows'}
              disabled={disabled}
              emptyLabel="— not stated: avoided unless the recipe clears it —"
              onChange={(next) => {
                set('jainExcluded', next === null ? null : next === 'avoids');
              }}
            />
          </div>

          <div className="flex flex-col gap-3">
            <Toggle
              id={onionId}
              checked={values.hasOnionGarlic}
              disabled={disabled}
              label="Contains onion or garlic"
              onChange={(next) => {
                set('hasOnionGarlic', next);
              }}
            />
            <Toggle
              id={beefId}
              checked={values.hasBeef}
              disabled={disabled}
              label="Contains beef — removes it from search entirely, for everyone"
              onChange={(next) => {
                set('hasBeef', next);
              }}
            />
          </div>

          <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/40 p-4">
            <span className={MICRO_LABEL}>Allergens</span>
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              An empty list can mean two different things, and the catalog keeps them apart. Until you mark
              this checked the food is treated as <strong className="font-medium">unassessed</strong> and
              withheld from anyone with a recorded allergy — which is the safe direction. Marking it checked
              tells plan generation the list below is complete, so do that only if you know it is.
            </p>
            <TokenPicker
              label="Contains"
              options={ALLERGENS}
              selected={values.allergens}
              disabled={disabled || !assessed}
              onToggle={(value: AllergenName) => {
                set('allergens', toggleToken(values.allergens, value, ALLERGENS));
              }}
            />
            <Toggle
              id={`${onionId}-assessed`}
              checked={assessed}
              disabled={disabled}
              label="I have checked this food's allergens and the list above is complete"
              onChange={(next) => {
                set('allergenSource', next ? 'source_stated' : 'unassessed');
              }}
            />
          </div>
        </div>
      </Panel>

      <Panel
        title="Catalog placement"
        description="How eagerly plans reach for this food. None of it excludes the food from search."
      >
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-3">
            <Toggle
              id={servableId}
              checked={values.servableAsIs}
              disabled={disabled}
              label="A person eats this on its own, rather than cooking with it"
              onChange={(next) => {
                set('servableAsIs', next);
              }}
            />
            <Toggle
              id={commonId}
              checked={values.isCommonInIndia}
              disabled={disabled}
              label="An Indian household can cook or order this for everyday eating"
              onChange={(next) => {
                set('isCommonInIndia', next);
              }}
            />
          </div>

          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
            <NullableSelect
              label="Meal role"
              hint="How it anchors a meal."
              options={MEAL_ROLES}
              value={values.mealRole}
              disabled={disabled}
              onChange={(next) => {
                set('mealRole', next);
              }}
            />
            <NullableSelect
              label="Prep time"
              options={PREP_TIME_BANDS}
              value={values.prepTimeBand}
              disabled={disabled}
              onChange={(next) => {
                set('prepTimeBand', next);
              }}
            />
            <NullableSelect
              label="Cost"
              options={COST_BANDS}
              value={values.costBand}
              disabled={disabled}
              onChange={(next) => {
                set('costBand', next);
              }}
            />
          </div>

          <TokenPicker
            label="Cuisines"
            hint="Up to four. “Universal” is for staples every regional table shares, and is used alone."
            options={FOOD_CUISINES}
            selected={values.cuisines}
            disabled={disabled}
            onToggle={(value: FoodCuisineName) => {
              set('cuisines', toggleToken(values.cuisines, value, FOOD_CUISINES));
            }}
          />

          <TokenPicker
            label="Meal times"
            hint="Leave empty if it suits any occasion — empty is not a gap here."
            options={FOOD_MEAL_TIMES}
            selected={values.mealTimes}
            disabled={disabled}
            onToggle={(value: FoodMealTimeName) => {
              set('mealTimes', toggleToken(values.mealTimes, value, FOOD_MEAL_TIMES));
            }}
          />

          <div className="flex flex-col gap-2">
            <span className={MICRO_LABEL}>Season</span>
            <span className="text-xs text-muted-foreground">
              Leave empty for anything available year round — most foods. Months picked here are the only ones
              plans will use it in.
            </span>
            <div role="group" aria-label="Season" className="flex flex-wrap gap-2">
              {MONTHS.map((month) => (
                <Button
                  key={month}
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-pressed={values.seasonMonths.includes(month)}
                  disabled={disabled}
                  onClick={() => {
                    const next = new Set(values.seasonMonths);
                    if (next.has(month)) next.delete(month);
                    else next.add(month);
                    set(
                      'seasonMonths',
                      [...next].sort((a, b) => a - b),
                    );
                  }}
                  className={cn(
                    'h-7 w-12 rounded-md px-2 text-xs font-normal text-muted-foreground',
                    values.seasonMonths.includes(month) &&
                      'border-primary bg-primary/10 font-medium text-primary hover:bg-primary/15 hover:text-primary',
                  )}
                >
                  {MONTH_LABELS[month]}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </Panel>

      <Panel
        title="Portions"
        description="What a kitchen actually serves this in, with the weight of ONE unit. A food with no household portion ranks last in the plan shortlist and reaches a plan only as a 100 g fallback."
      >
        <PortionsEditor
          portions={values.portions}
          disabled={disabled}
          onChange={(next) => {
            set('portions', next);
          }}
        />
      </Panel>

      <Panel
        title="Serving policy"
        description="How many of those portions a meal may hold. Leave it off unless this food needs different numbers from its role's default."
      >
        {values.servingPolicy === null ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => {
              set('servingPolicy', { min: '0.5', typical: '1', max: '2', maxDay: '', elastic: true });
            }}
          >
            Override the default
          </Button>
        ) : (
          <ServingPolicyEditor
            policy={values.servingPolicy}
            disabled={disabled}
            onChange={(next) => {
              set('servingPolicy', next);
            }}
          />
        )}
      </Panel>

      <Panel
        title="Aliases"
        description="Other names this is known by, including Indian-language ones. Search matches these as well as the name."
      >
        <div className="flex flex-col gap-3">
          {values.aliases.map((alias, index) => (
            <div key={index} className="flex flex-wrap items-end gap-3">
              <div className="min-w-[200px] flex-1">
                <Input
                  aria-label={`Alias ${String(index + 1)}`}
                  value={alias.alias}
                  disabled={disabled}
                  placeholder="Paneer tikka"
                  onChange={(event) => {
                    const next = [...values.aliases];
                    next[index] = { ...alias, alias: event.target.value };
                    set('aliases', next);
                  }}
                />
              </div>
              <div className="w-24">
                <Input
                  aria-label={`Alias ${String(index + 1)} language`}
                  value={alias.language}
                  disabled={disabled}
                  placeholder="hi"
                  onChange={(event) => {
                    const next = [...values.aliases];
                    next[index] = { ...alias, language: event.target.value };
                    set('aliases', next);
                  }}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => {
                  set(
                    'aliases',
                    values.aliases.filter((_, at) => at !== index),
                  );
                }}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-fit"
            disabled={disabled}
            onClick={() => {
              set('aliases', [...values.aliases, { alias: '', language: '' }]);
            }}
          >
            Add an alias
          </Button>
        </div>
      </Panel>

      {values.kind !== 'dish' ? null : (
        <Panel
          title="Recipe"
          description="What the dish is made of. Optional, but a line pointed at a catalog ingredient is what lets the dish's allergens and diet class be re-derived later if that ingredient is corrected. A draft's lines are matched to the catalog by name; an amber one was matched by nearest name only and needs a look."
        >
          <RecipeEditor
            values={values}
            disabled={disabled}
            onChange={(next) => {
              onChange(next);
            }}
          />
        </Panel>
      )}
    </div>
  );
}

interface NumberFieldProps {
  readonly label: string;
  readonly hint?: string;
  readonly value: string;
  readonly disabled: boolean;
  readonly onChange: (next: string) => void;
}

/**
 * Held as text rather than a number, so an emptied field stays empty. `Number('')`
 * is zero, and a form that saved that would let someone publish a food claiming
 * no calories because they cleared the box to retype it.
 */
function NumberField({ label, hint, value, disabled, onChange }: NumberFieldProps) {
  const id = useId();

  return (
    <Labelled id={id} label={label} {...(hint === undefined ? {} : { hint })}>
      <Input
        id={id}
        type="number"
        min={0}
        step="any"
        value={value}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </Labelled>
  );
}

interface ToggleProps {
  readonly id: string;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly label: string;
  readonly onChange: (next: boolean) => void;
}

function Toggle({ id, checked, disabled, label, onChange }: ToggleProps) {
  return (
    <div className="flex items-center gap-3">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => {
          onChange(next === true);
        }}
      />
      <Label htmlFor={id} className="font-normal text-foreground">
        {label}
      </Label>
    </div>
  );
}

interface ServingPolicyEditorProps {
  readonly policy: ServingPolicyValues;
  readonly disabled: boolean;
  readonly onChange: (next: ServingPolicyValues | null) => void;
}

/**
 * Its own component so the policy is narrowed once, at the boundary, rather
 * than re-asserted non-null in each of five handlers.
 */
function ServingPolicyEditor({ policy, disabled, onChange }: ServingPolicyEditorProps) {
  const elasticId = useId();

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-4">
        <NumberField
          label="Min"
          value={policy.min}
          disabled={disabled}
          onChange={(next) => {
            onChange({ ...policy, min: next });
          }}
        />
        <NumberField
          label="Typical"
          value={policy.typical}
          disabled={disabled}
          onChange={(next) => {
            onChange({ ...policy, typical: next });
          }}
        />
        <NumberField
          label="Max"
          value={policy.max}
          disabled={disabled}
          onChange={(next) => {
            onChange({ ...policy, max: next });
          }}
        />
        <NumberField
          label="Daily cap"
          hint="Blank for none"
          value={policy.maxDay}
          disabled={disabled}
          onChange={(next) => {
            onChange({ ...policy, maxDay: next });
          }}
        />
      </div>
      <Toggle
        id={elasticId}
        checked={policy.elastic}
        disabled={disabled}
        label="The solver may stretch this to hit a target"
        onChange={(next) => {
          onChange({ ...policy, elastic: next });
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit"
        disabled={disabled}
        onClick={() => {
          onChange(null);
        }}
      >
        Use the role default instead
      </Button>
    </div>
  );
}

interface PortionsEditorProps {
  readonly portions: PortionValues[];
  readonly disabled: boolean;
  readonly onChange: (next: PortionValues[]) => void;
}

function PortionsEditor({ portions, disabled, onChange }: PortionsEditorProps) {
  return (
    <div className="flex flex-col gap-3">
      {portions.map((portion, index) => (
        <div key={index} className="flex flex-wrap items-end gap-3">
          <div className="min-w-[160px] flex-1">
            <Input
              aria-label={`Portion ${String(index + 1)} label`}
              value={portion.label}
              disabled={disabled}
              placeholder="katori"
              onChange={(event) => {
                const next = [...portions];
                next[index] = { ...portion, label: event.target.value };
                onChange(next);
              }}
            />
          </div>
          <select
            aria-label={`Portion ${String(index + 1)} unit`}
            value={portion.unit}
            disabled={disabled}
            onChange={(event) => {
              const next = [...portions];
              next[index] = { ...portion, unit: event.target.value as PortionValues['unit'] };
              onChange(next);
            }}
            className={cn(NATIVE_SELECT, 'w-32')}
          >
            {PORTION_UNITS.map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
          <div className="w-28">
            <Input
              aria-label={`Portion ${String(index + 1)} grams`}
              type="number"
              min={0}
              step="any"
              value={portion.grams}
              disabled={disabled}
              placeholder="g"
              onChange={(event) => {
                const next = [...portions];
                next[index] = { ...portion, grams: event.target.value };
                onChange(next);
              }}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={portion.isDefault}
            disabled={disabled}
            onClick={() => {
              // Exactly one default, so picking one clears the rest rather
              // than leaving the operator to notice.
              onChange(portions.map((row, at) => ({ ...row, isDefault: at === index })));
            }}
            className={cn(portion.isDefault && 'border-primary bg-primary/10 text-primary')}
          >
            Default
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => {
              onChange(portions.filter((_, at) => at !== index));
            }}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit"
        disabled={disabled}
        onClick={() => {
          onChange([...portions, { label: '', unit: 'katori', grams: '', isDefault: portions.length === 0 }]);
        }}
      >
        Add a portion
      </Button>
    </div>
  );
}

interface RecipeEditorProps {
  readonly values: FoodFormValues;
  readonly disabled: boolean;
  readonly onChange: (next: FoodFormValues) => void;
}

function RecipeEditor({ values, disabled, onChange }: RecipeEditorProps) {
  const noteId = useId();
  const setLines = (lines: RecipeLineValues[]) => {
    onChange({ ...values, recipeLines: lines });
  };

  return (
    <div className="flex flex-col gap-4">
      {values.recipeLines.map((line, index) => (
        <div key={index} className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1">
            <Input
              aria-label={`Ingredient ${String(index + 1)}`}
              value={line.rawName}
              disabled={disabled}
              placeholder="Paneer"
              onChange={(event) => {
                const next = [...values.recipeLines];
                next[index] = { ...line, rawName: event.target.value };
                setLines(next);
              }}
            />
          </div>
          <div className="w-28">
            <Input
              aria-label={`Ingredient ${String(index + 1)} grams`}
              type="number"
              min={0}
              step="any"
              value={line.grams}
              disabled={disabled}
              placeholder="g"
              onChange={(event) => {
                const next = [...values.recipeLines];
                next[index] = { ...line, grams: event.target.value };
                setLines(next);
              }}
            />
          </div>
          <div className="min-w-[220px] flex-1">
            <IngredientPicker
              disabled={disabled}
              value={
                line.ingredientFoodId === null
                  ? null
                  : { id: line.ingredientFoodId, name: line.ingredientName ?? line.rawName }
              }
              onChange={(next) => {
                const updated = [...values.recipeLines];
                // A person's pick is not a matcher tier, so the badge stops
                // describing how the model's line was resolved.
                updated[index] = {
                  ...line,
                  ingredientFoodId: next?.id ?? null,
                  ingredientName: next?.name ?? null,
                  matchedBy: null,
                };
                setLines(updated);
              }}
            />
          </div>
          <LineMatchBadge line={line} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => {
              setLines(values.recipeLines.filter((_, at) => at !== index));
            }}
          >
            Remove
          </Button>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit"
        disabled={disabled}
        onClick={() => {
          setLines([
            ...values.recipeLines,
            {
              rawName: '',
              ingredientFoodId: null,
              ingredientName: null,
              matchedBy: null,
              grams: '',
              retentionGroup: '',
            },
          ]);
        }}
      >
        Add an ingredient
      </Button>

      {values.recipeLines.length === 0 ? null : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
          <SelectField
            label="Confidence"
            hint="Your own confidence in these lines."
            options={RECIPE_CONFIDENCES}
            value={values.recipeConfidence}
            disabled={disabled}
            onChange={(next) => {
              onChange({ ...values, recipeConfidence: next });
            }}
          />
          <Labelled id={noteId} label="Note" hint="Where the lines come from.">
            <Textarea
              id={noteId}
              rows={2}
              value={values.recipeNote}
              disabled={disabled}
              onChange={(event) => {
                onChange({ ...values, recipeNote: event.target.value });
              }}
            />
          </Labelled>
        </div>
      )}
    </div>
  );
}

const MATCH_BADGES: Readonly<Record<RecipeLineMatchName, { tone: BadgeTone; label: string; title: string }>> =
  {
    exact: { tone: 'ok', label: 'matched', title: 'The catalog name or one of its aliases, verbatim.' },
    precedent: {
      tone: 'info',
      label: 'matched (recipes)',
      title: 'What reviewed recipes already pin this name to.',
    },
    closest: {
      tone: 'warn',
      label: 'nearest name, check it',
      title:
        'The nearest search hit, verified only by a shared word. Change it if it is not this ingredient.',
    },
  };

/**
 * How a line came to point where it points. Amber is the one that needs a
 * look: the server matched it by nearest name and a shared word, which is
 * enough to be usually right and not enough to be trusted unread.
 */
function LineMatchBadge({ line }: { readonly line: RecipeLineValues }) {
  const badge =
    line.ingredientFoodId === null
      ? { tone: 'err' as const, label: 'unmatched', title: 'Pick a catalog ingredient or remove the line.' }
      : line.matchedBy === null
        ? { tone: 'ok' as const, label: 'chosen', title: 'Picked by you.' }
        : MATCH_BADGES[line.matchedBy];

  return (
    <Badge size="sm" tone={badge.tone} title={badge.title} className="mb-2.5">
      {badge.label}
    </Badge>
  );
}
